import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  EMPTY,
  defer,
  distinctUntilChanged,
  fromEvent,
  map,
  repeat,
  retry,
  startWith,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import { OverlayBadgeModule } from 'primeng/overlaybadge';

import { NgIconComponent } from '@ng-icons/core';

import { NotificationsList } from '@layouts/dashboard/components/header/components/notifications/notifications-list/notifications-list.component';

import { NotificationState } from '@services/state/notification.state';

/** Gap between polls while notifications are arriving. */
const BASE_POLL_MS = 60_000;

/** Ceiling for the backoff, so an idle tab still notices within 5 minutes. */
const MAX_POLL_MS = 300_000;

@Component({
  selector: 'notifications',
  standalone: true,
  imports: [OverlayBadgeModule, NgIconComponent, NotificationsList],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent {
  private notificationState = inject(NotificationState);
  private document = inject(DOCUMENT);
  private notificationsCount = this.notificationState.notificationCount;

  /** Current gap between polls; grows while the count sits still. */
  private pollDelayMs = BASE_POLL_MS;
  private lastUnreadCount: number | undefined;

  public unreadCountBadge = computed(() => {
    if (!this.notificationsCount() || !this.notificationsCount()?.unread_count)
      return 0;
    return this.notificationsCount()!.unread_count > 9
      ? '9+'
      : this.notificationsCount()!.unread_count;
  });

  constructor() {
    this.notificationState.setNotifications();
    this.pollUnreadCount().pipe(takeUntilDestroyed()).subscribe();
  }

  /**
   * Replaces a recursive setTimeout that ran every 60 s for the life of the
   * session (LEN-2065). Two things it did not do:
   *
   * - Stop while the tab was hidden. The dashboard layout is never recreated,
   *   so a backgrounded tab kept polling all day.
   * - Slow down. The interval was fixed whether or not anything was arriving.
   */
  private pollUnreadCount() {
    return fromEvent(this.document, 'visibilitychange').pipe(
      startWith(null),
      map(() => this.document.visibilityState === 'visible'),
      distinctUntilChanged(),
      switchMap(isVisible => {
        if (!isVisible) {
          // No timer at all while hidden, rather than a timer that fires and
          // discards its result.
          return EMPTY;
        }

        // Back from a hidden tab: ask straight away, and from the base gap --
        // whatever it had backed off to is stale information. lastUnreadCount
        // goes too: without it the first poll back compares against a count
        // from before the tab was hidden, matches, and doubles the gap
        // immediately instead of staying at the base one.
        this.pollDelayMs = BASE_POLL_MS;
        this.lastUnreadCount = undefined;

        return defer(() => this.notificationState.refreshCount()).pipe(
          tap(count => this.pace(count.unread_count)),
          // retry before repeat: a failed poll keeps polling instead of ending
          // the stream, and a successful one schedules the next gap.
          retry({
            delay: () => {
              // Back off on failure too. pace() only runs on success, so
              // without this an outage left every open tab retrying at the
              // base gap for as long as it lasted.
              this.backOff();
              return timer(this.pollDelayMs);
            },
          }),
          repeat({ delay: () => timer(this.pollDelayMs) })
        );
      })
    );
  }

  /** Resets the gap when the count moves, doubles it while it does not. */
  private pace(unreadCount: number) {
    if (unreadCount === this.lastUnreadCount) {
      this.backOff();
    } else {
      this.pollDelayMs = BASE_POLL_MS;
    }

    this.lastUnreadCount = unreadCount;
  }

  /** Doubles the gap, never past the ceiling. */
  private backOff() {
    this.pollDelayMs = Math.min(this.pollDelayMs * 2, MAX_POLL_MS);
  }

  /**
   * In this function it opens the notification list and also makes the http call to mark as viewed the notification
   * @param event
   */
  public toggleNotificationMenu(event: Event) {
    this.notificationState.toggleMenu(event);
  }
}
